/**
 * Client-side draft for the tehtäväpohja editor.
 *
 * Edits accumulate until Tallenna flushes them through one mutation, so
 * a new päätehtävä and the alitehtävät added under it in the same
 * session can be saved together; the parent link travels as a tempId the
 * server resolves.
 */

import { useCallback, useMemo, useState } from "react";
import type { DeadlineOffset } from "./deadlineOffset";
import { offsetToColumns } from "./deadlineOffset";
import type { TemplateTaskKind } from "./templateTaskCatalog";

/** What the save mutation's `kind` field accepts. */
export type DraftTaskKind = TemplateTaskKind | "SUBTASK";

export interface DraftTaskPatch {
  description?: string | null;
  congridInspectionTemplateId?: string | null;
  name?: string;
  offset?: DeadlineOffset;
}

export interface DraftNewTask {
  tempId: string;
  name: string;
  kind: DraftTaskKind;
  templateProcurementPackageId: string | null;
  templateWorkSectionId: string | null;
  parentTempId: string | null;
  parentTemplateTaskId: string | null;
  description: string | null;
  congridInspectionTemplateId: string | null;
  offset: DeadlineOffset;
}

let tempCounter = 0;
export function nextTempId(): string {
  tempCounter += 1;
  return `temp-${tempCounter}`;
}

export function useTemplateSetDraft() {
  const [patches, setPatches] = useState<Record<string, DraftTaskPatch>>({});
  const [newTasks, setNewTasks] = useState<DraftNewTask[]>([]);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);

  const patchTask = useCallback((id: string, patch: DraftTaskPatch) => {
    setPatches((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  const patchNewTask = useCallback((tempId: string, patch: DraftTaskPatch) => {
    setNewTasks((prev) =>
      prev.map((t) =>
        t.tempId === tempId
          ? {
              ...t,
              name: patch.name ?? t.name,
              description:
                patch.description !== undefined
                  ? patch.description
                  : t.description,
              congridInspectionTemplateId:
                patch.congridInspectionTemplateId !== undefined
                  ? patch.congridInspectionTemplateId
                  : t.congridInspectionTemplateId,
              offset: patch.offset ?? t.offset,
            }
          : t
      )
    );
  }, []);

  const addTask = useCallback((task: Omit<DraftNewTask, "tempId">) => {
    const tempId = nextTempId();
    setNewTasks((prev) => [...prev, { ...task, tempId }]);
    return tempId;
  }, []);

  const removeNewTask = useCallback((tempId: string) => {
    setNewTasks((prev) =>
      prev.filter((t) => t.tempId !== tempId && t.parentTempId !== tempId)
    );
  }, []);

  const deleteExisting = useCallback((id: string) => {
    setDeletedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setNewTasks((prev) => prev.filter((t) => t.parentTemplateTaskId !== id));
    setPatches((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const undeleteExisting = useCallback((id: string) => {
    setDeletedIds((prev) => prev.filter((d) => d !== id));
  }, []);

  const reset = useCallback(() => {
    setPatches({});
    setNewTasks([]);
    setDeletedIds([]);
  }, []);

  const isDirty = useMemo(
    () =>
      Object.keys(patches).length > 0 ||
      newTasks.length > 0 ||
      deletedIds.length > 0,
    [patches, newTasks, deletedIds]
  );

  const toSavePayload = useCallback(
    (versions: Record<string, number>) => {
      const creates = newTasks.map((t) => {
        const cols = offsetToColumns(t.offset);
        return {
          tempId: t.tempId,
          name: t.name,
          kind: t.kind,
          templateProcurementPackageId: t.templateProcurementPackageId,
          templateWorkSectionId: t.templateWorkSectionId,
          parentTempId: t.parentTempId,
          parentTemplateTaskId: t.parentTemplateTaskId,
          description: t.description,
          congridInspectionTemplateId: t.congridInspectionTemplateId,
          startTimingOffsetDays: cols.startTimingOffsetDays,
          endTimingOffsetDays: cols.endTimingOffsetDays,
        };
      });

      const updates = Object.entries(patches).map(([id, patch]) => {
        const update: Record<string, unknown> = {
          id,
          version: versions[id] ?? 0,
        };
        if (patch.name !== undefined) update.name = patch.name;
        if (patch.description !== undefined)
          update.description = patch.description;
        if (patch.congridInspectionTemplateId !== undefined) {
          update.congridInspectionTemplateId = patch.congridInspectionTemplateId;
        }
        if (patch.offset !== undefined) {
          const cols = offsetToColumns(patch.offset);
          update.startTimingOffsetDays = cols.startTimingOffsetDays;
          update.endTimingOffsetDays = cols.endTimingOffsetDays;
        }
        return update;
      });

      return { creates, updates, deleteIds: deletedIds };
    },
    [patches, newTasks, deletedIds]
  );

  return {
    isDirty,
    patches,
    newTasks,
    deletedIds,
    patchTask,
    patchNewTask,
    addTask,
    removeNewTask,
    deleteExisting,
    undeleteExisting,
    reset,
    toSavePayload,
  };
}
